import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";

// Soft UI Dashboard React examples
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import DropdownMenuItem from "examples/Items/DropdownMenuItem";
import useConstants from "constants";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Soft UI Dashboard React context
import { useSoftUIController, setTransparentNavbar, setMiniSidenav, setDirection } from "context";

// Images
import team2 from "assets/images/team-2.jpg";
import logoSpotify from "assets/images/small-logos/logo-spotify.svg";
import TranslateIcon from "@mui/icons-material/Translate";
import useLanguageSwitcher from "../../../hooks/useLanguageSwitcher";
import useAuth from "hooks/useAuth";
import useAPI from "hooks/useAPI";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { Tooltip } from "@mui/material";
import colors from "assets/theme/base/colors";

function DashboardNavbar({ absolute, light, isMini, pagesList }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [openLanguageMenu, setOpenLanguageMenu] = useState(false);
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const { switchLanguage, getActiveLang } = useLanguageSwitcher();
  const { logout, user, cleanUp } = useAuth();
  const { api } = useAPI();
  const [openloader, setopenloader] = useState(false);
  const { ERM_APP_URL, ADMIN_URL } = useConstants();

  const PageHeader = () => {
    return <Breadcrumbs pagesList={pagesList} />;
  };

  const { dark } = colors;

  const Username = () => {
    var name = "";
    if (user?.first_name) {
      name += user.first_name;
    } else {
      name += user?.username;
    }
    return (
      <SoftTypography variant="h6" color="dark" sx={{ display: "inline-block" }}>
        {name}
      </SoftTypography>
    );
  };

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleOpenUserMenu = (event) => setOpenUserMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleCloseUserMenu = () => setOpenUserMenu(false);
  const handleOpenLanguageMenu = (event) => setOpenLanguageMenu(event.currentTarget);
  const handleCloseLanguageMenu = () => setOpenLanguageMenu(false);

  // Change the language
  const handleLanguageChange = async (event) => {
    try {
      const selectedLanguage = event.target.value;
      if (getActiveLang() === selectedLanguage) return;
      setopenloader(true);
      var response = await api.post(`/set-language/`, { language: selectedLanguage });
      if (response.status == 200) {
        switchLanguage(selectedLanguage);
        setopenloader(false);
      } else {
        console.warn("Unexpected response from server:", response);
        setopenloader(false);
      }
    } catch (error) {
      console.error("Error changing language:", error.message);
      setopenloader(false);
    }
  };

  const goToUlmProfile = () => {
    cleanUp();
    window.location.assign(`${ERM_APP_URL}/profile`);
  };

  // Render the user menu
  const renderUserMenu = () => (
    <Menu
      anchorEl={openUserMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openUserMenu)}
      onClose={handleCloseUserMenu}
      sx={{ mt: 2 }}
    >
      <DropdownMenuItem
        icon={<Icon>person</Icon>}
        title={t("lang.profile")}
        onClick={() => goToUlmProfile()}
      />
      <DropdownMenuItem
        icon={<Icon>logout</Icon>}
        title={t("lang.logout")}
        onClick={handleLogout}
      />
    </Menu>
  );

  const handleLogout = () => {
    logout();
  };

  // Render the language menu
  const renderLanguageMenu = () => (
    <Menu
      anchorEl={openLanguageMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openLanguageMenu)}
      onClose={handleCloseLanguageMenu}
      sx={{ mt: 2 }}
    >
      <MenuItem onClick={() => handleLanguageChange({ target: { value: "en" } })}>English</MenuItem>
      <MenuItem onClick={() => handleLanguageChange({ target: { value: "he" } })}>עִברִית</MenuItem>
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || dark.main;

      if (transparentNavbar && !light) {
        colorValue = text.main;
      }

      return colorValue;
    },
  });

  return (
    <>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={openloader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <AppBar
        position={absolute ? "absolute" : navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
      >
        <Toolbar sx={(theme) => navbarContainer(theme)}>
          <SoftBox
            color="inherit"
            mb={{ xs: 1, md: 0 }}
            sx={(theme) => navbarRow(theme, { isMini })}
          >
            <PageHeader />
          </SoftBox>
          {isMini ? null : (
            <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
              <SoftBox color={light ? "white" : "inherit"}>
                <Tooltip title={t("lang.back_to_aam")} placement="top">
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    sx={navbarIconButton}
                    aria-controls="back-to-aam"
                    aria-haspopup="true"
                    variant="contained"
                    onClick={() => cleanUp(ADMIN_URL)}
                  >
                    <Icon>exit_to_app</Icon>
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  aria-controls="language-menu"
                  aria-haspopup="true"
                  variant="contained"
                  onClick={handleOpenLanguageMenu}
                >
                  <TranslateIcon sx={iconsStyle} />
                </IconButton>
                {renderLanguageMenu()}
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                >
                  <Icon>{miniSidenav ? "menu_open" : "menu"}</Icon>
                </IconButton>
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  aria-controls="user-menu"
                  aria-haspopup="true"
                  variant="contained"
                  onClick={handleOpenUserMenu}
                >
                  <Icon>account_circle</Icon>
                  <Username />
                </IconButton>
                {renderUserMenu()}
              </SoftBox>
            </SoftBox>
          )}
        </Toolbar>
      </AppBar>
    </>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  pagesList: [],
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  pagesList: PropTypes.array,
};

export default DashboardNavbar;
